/**
 * API Key Authentication Provider Module
 * Provides authentication using API keys for embed functionality
 * Mimics the session provider interface for seamless integration
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { api } from '@/utils/api-client'
import { setAuthTokens, clearAuthTokens } from '@/utils/auth-storage'
import LoadingSpinner from './loading-spinner'

/**
 * Props for the ApiKeyProvider component
 * @interface ApiKeyProviderProps
 * @property {string} apiKey - The API key to authenticate with
 * @property {ReactNode} children - Child components to render after authentication
 */
interface ApiKeyProviderProps {
  apiKey: string
  children: ReactNode
}

/**
 * User data structure from API key authentication
 * Simplified version compatible with session auth
 * @interface ApiKeyUser
 * @property {string} id - User ID
 * @property {string} name - User display name
 * @property {string} email - User email address
 */
interface ApiKeyUser {
  id: string
  name: string
  email: string
}

/**
 * Workspace data structure from API key authentication
 * Compatible with session auth workspace interface
 * @interface ApiKeyWorkspace
 * @property {string} id - Workspace ID
 * @property {string} name - Workspace name
 */
interface ApiKeyWorkspace {
  id: string
  name: string
}

/**
 * Context for API key authentication state
 * @interface ApiKeyAuthContext
 * @property {ApiKeyUser | null} user - Authenticated user data
 * @property {ApiKeyWorkspace | null} workspace - Current workspace data
 * @property {boolean} isAuthenticated - Whether authentication is successful
 * @property {boolean} isLoading - Whether authentication is in progress
 * @property {string | null} error - Authentication error message
 */
interface ApiKeyAuthContext {
  user: ApiKeyUser | null
  workspace: ApiKeyWorkspace | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

/**
 * Default context values for API key authentication
 * Used when the context is accessed outside of a provider
 */
const defaultContextValue: ApiKeyAuthContext = {
  user: null,
  workspace: null,
  isAuthenticated: false,
  isLoading: false,
  error: null
}

/**
 * React context for API key authentication state
 * Provides authentication state to child components
 */
const ApiKeyAuthContext = createContext<ApiKeyAuthContext>(defaultContextValue)

/**
 * Hook to access API key authentication context
 * @returns {ApiKeyAuthContext} The current API key authentication state
 * @throws {Error} When used outside of ApiKeyProvider
 */
export function useApiKeyAuth(): ApiKeyAuthContext {
  const context = useContext(ApiKeyAuthContext)

  if (!context) {
    throw new Error('useApiKeyAuth must be used within an ApiKeyProvider')
  }

  return context
}

/**
 * API Key Authentication Provider Component
 * Authenticates using an API key and provides user/workspace data to children
 * Compatible with existing session-based authentication patterns
 *
 * @param {ApiKeyProviderProps} props - Component props
 * @returns {JSX.Element} Provider component with API key authentication
 */
export function ApiKeyProvider({ apiKey, children }: ApiKeyProviderProps) {
  /**
   * Authenticated user data
   * Set after successful API key validation
   */
  const [user, setUser] = useState<ApiKeyUser | null>(null)

  /**
   * Current workspace data
   * Associated with the API key
   */
  const [workspace, setWorkspace] = useState<ApiKeyWorkspace | null>(null)

  /**
   * Loading state during authentication
   * True while validating the API key
   */
  const [isLoading, setIsLoading] = useState(true)

  /**
   * Authentication error state
   * Contains error message if authentication fails
   */
  const [error, setError] = useState<string | null>(null)

  /**
   * Whether authentication was successful
   * True when user and workspace are available
   */
  const isAuthenticated = user !== null && workspace !== null

  /**
   * Exchange API key for Bearer token and validate session
   * New approach: API key → Bearer token → session validation
   */
  useEffect(() => {
    const exchangeApiKeyForToken = async () => {
      try {
        setIsLoading(true)
        setError(null)


        // Step 1: Exchange API key for Bearer token
        const tokenResponse = await api.post('/auth/api-key-token', {
          apiKey: apiKey
        }, {
          requiresAuth: false,
          showErrorToast: false
        })


        if (!tokenResponse.success || !tokenResponse.data) {
          throw new Error(tokenResponse.error || 'Failed to exchange API key for token')
        }

        const { accessToken, refreshToken, expiresAt } = tokenResponse.data

        // Step 2: Store tokens in localStorage (same as normal login)
        setAuthTokens({
          accessToken,
          refreshToken,
          expiresAt: new Date(expiresAt).getTime() // Convert ISO string to timestamp
        })

        // Step 3: Validate session to get user/workspace data
        const sessionResponse = await api.get('/auth/session', {
          requiresAuth: true,
          showErrorToast: false
        })


        if (sessionResponse.success && sessionResponse.data) {
          // Extract user and workspace data from session
          const { user: userData, workspace: workspaceData } = sessionResponse.data


          setUser({
            id: userData.id,
            name: userData.name || userData.email,
            email: userData.email
          })

          setWorkspace({
            id: workspaceData.id,
            name: workspaceData.name
          })

        } else {
          throw new Error('Failed to validate session after token exchange')
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed'
        setError(errorMessage)
        setUser(null)
        setWorkspace(null)
        // Clear any partial tokens on failure
        clearAuthTokens()
      } finally {
        setIsLoading(false)
      }
    }

    // Only exchange if we have an API key
    if (apiKey) {
      exchangeApiKeyForToken()
    } else {
      setError('No API key provided')
      setIsLoading(false)
    }
  }, [apiKey])

  /**
   * Context value to provide to child components
   * Contains all authentication state
   */
  const contextValue: ApiKeyAuthContext = {
    user,
    workspace,
    isAuthenticated,
    isLoading,
    error
  }

  // Show loading spinner during authentication
  if (isLoading) {
    return <LoadingSpinner message="Authenticating with API key..." />
  }

  // Show error state if authentication failed
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-6 max-w-md">
          <h2 className="text-lg font-semibold mb-2 text-destructive">
            Authentication Failed
          </h2>
          <p className="text-muted-foreground mb-4">
            {error}
          </p>
          <p className="text-xs text-muted-foreground">
            Please check your API key and try again.
          </p>
        </div>
      </div>
    )
  }

  // Show error if authentication didn't complete successfully
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-6 max-w-md">
          <h2 className="text-lg font-semibold mb-2 text-destructive">
            Authentication Error
          </h2>
          <p className="text-muted-foreground">
            Unable to authenticate with the provided API key.
          </p>
        </div>
      </div>
    )
  }

  // Render children with authentication context
  return (
    <ApiKeyAuthContext.Provider value={contextValue}>
      {children}
    </ApiKeyAuthContext.Provider>
  )
}

ApiKeyProvider.displayName = 'ApiKeyProvider'